import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * The behaviour of the top of the Ellaise product page (sections/ell-product.liquid):
 * the gallery, the bar that follows the page, the rows that open, and the redraw of
 * the price when another variant is picked.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * @typedef {object} GalleryRefs
 * @property {HTMLElement} view - The scroller that holds the slides.
 * @property {HTMLElement[]} [slides] - The slides.
 * @property {HTMLElement} [strip] - The scroller that holds the thumbnails.
 * @property {HTMLButtonElement[]} [thumbs] - The thumbnails.
 */

/**
 * The product gallery: slides that are swiped or picked from a strip of thumbnails.
 * A clip plays, muted, while its slide is the one in view.
 *
 * @extends {Component<GalleryRefs>}
 */
class EllGallery extends Component {
  requiredRefs = ['view'];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#watchSlides();
    for (const video of this.querySelectorAll('.ell-gal__slide video')) {
      const slide = video.closest('.ell-gal__slide');
      video.addEventListener('playing', () => slide?.classList.add('is-playing'), { signal });
      video.addEventListener('pause', () => slide?.classList.remove('is-playing'), { signal });
    }
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#watchSlides();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#abort?.abort();
  }

  /**
   * Brings a slide into view. A thumbnail calls it with its position.
   * @param {number} index - The position of the slide, from 0.
   */
  show(index) {
    const slide = this.refs.slides?.[index];
    if (!slide) return;

    this.refs.view.scrollTo({ left: slide.offsetLeft, behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
  }

  /**
   * Plays or pauses the clip of a slide. The button over the clip calls it.
   * @param {Event} event - The click.
   */
  toggleClip(event) {
    if (!(event.target instanceof Element)) return;

    const video = event.target.closest('.ell-gal__slide')?.querySelector('video');
    if (!video) return;

    if (video.paused) {
      this.#play(video);
    } else {
      video.pause();
    }
  }

  /** Watches which slide fills the viewer. */
  #watchSlides() {
    this.#observer?.disconnect();
    this.#observer = new IntersectionObserver(this.#onSlides, { root: this.refs.view, threshold: 0.6 });
    for (const slide of this.refs.slides ?? []) this.#observer.observe(slide);
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onSlides = (entries) => {
    for (const entry of entries) {
      const slide = /** @type {HTMLElement} */ (entry.target);
      const video = slide.querySelector('video');

      if (!entry.isIntersecting) {
        video?.pause();
        continue;
      }

      const index = (this.refs.slides ?? []).indexOf(slide);
      this.#setActive(index);
      if (video) this.#play(video);
    }
  };

  /**
   * Marks the slide in view and its thumbnail, and keeps that thumbnail in sight.
   * @param {number} index - The position of the slide, from 0.
   */
  #setActive(index) {
    const { slides = [], thumbs = [], strip } = this.refs;

    slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index);
      // the play button of a clip that is out of view is not a stop for the Tab key
      slide.querySelector('.ell-gal__play')?.setAttribute('tabindex', i === index ? '0' : '-1');
    });
    thumbs.forEach((thumb, i) => {
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    const item = thumbs[index]?.parentElement;
    if (!strip || !item) return;

    const start = item.offsetLeft;
    const end = start + item.offsetWidth;
    const seen = start >= strip.scrollLeft && end <= strip.scrollLeft + strip.clientWidth - 10;
    if (!seen) strip.scrollTo({ left: start, behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
  }

  /** @param {HTMLVideoElement} video */
  #play(video) {
    video.muted = true;
    video.play().catch(() => {
      // The browser refused to start it. The play button stays on the clip.
    });
  }

  /**
   * Goes to the picture of the variant that was picked, when it has one.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        const mediaId = detail?.resource?.featured_media?.id;
        if (!mediaId) return;

        const index = (this.refs.slides ?? []).findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index >= 0) this.show(index);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[ell-gallery] Event promise rejected:', error);
      });
  };
}

/**
 * The bar that follows the page with the price and a button, whenever the buy button of
 * the buy box is out of sight, above the window or below it. Its button presses that buy
 * button.
 *
 * @extends {Component}
 */
class EllSticky extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {ResizeObserver | undefined} */
  #resize;

  connectedCallback() {
    super.connectedCallback();

    const button = this.#buyButton;
    if (!button) return;

    this.#observer = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      this.classList.toggle('is-on', !entry.isIntersecting);
    });
    this.#observer.observe(button);

    // the footer leaves room for the bar, so that the bar never covers its last line
    this.#resize = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--ell-sticky-height', `${Math.max(0, this.offsetHeight - 1)}px`);
    });
    this.#resize.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#resize?.disconnect();
    document.documentElement.style.removeProperty('--ell-sticky-height');
  }

  /** Presses the buy button of the buy box. The bar's button calls it. */
  add() {
    this.#buyButton?.click();
  }

  /** @returns {HTMLButtonElement | null} */
  get #buyButton() {
    return this.closest('.shopify-section')?.querySelector('product-form-component button[type="submit"]') ?? null;
  }
}

/**
 * The top of the product page. It redraws the parts of its section that show the selected
 * variant: each element that carries data-ell-live takes the content of its twin in the
 * page the theme's variant picker fetched. It also shows the total of the picked offer
 * (blocks/ell-offers.liquid) in the bar that follows the page, and unlocks the tiles
 * that the picked offer opens.
 *
 * @extends {Component}
 */
class EllProduct extends Component {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const section = this.closest('.shopify-section');

    section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    section?.addEventListener('change', this.#onChange, { signal });
    this.#showOffer();
    this.#showUnlocks();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  /** @param {Event} event */
  #onChange = (event) => {
    if (!(event.target instanceof Element) || !event.target.matches('.ell-offers__input')) return;

    this.#showOffer();
    this.#showUnlocks();
  };

  /**
   * Locks and unlocks the tiles under the offers (snippets/ell-deal.liquid). Each tile carries
   * how many the customer has to buy to open it, and is open once the picked offer buys that many.
   */
  #showUnlocks() {
    const section = this.closest('.shopify-section');
    const picked = section?.querySelector('.ell-offers__input:checked');
    if (!section || !(picked instanceof HTMLInputElement)) return;

    const quantity = Number(picked.value) || 1;

    for (const tile of section.querySelectorAll('[data-ell-unlock-from]')) {
      if (!(tile instanceof HTMLElement)) continue;

      const locked = quantity < Number(tile.dataset.ellUnlockFrom);
      // the animation plays on the tile that this choice has just opened
      tile.classList.toggle('is-opening', !locked && tile.classList.contains('is-locked'));
      tile.classList.toggle('is-locked', locked);
    }
  }

  /**
   * Prints the total of the picked offer in the bar that follows the page. The bar's button
   * adds what is picked, so its price is the price of that many.
   */
  #showOffer() {
    const section = this.closest('.shopify-section');
    const picked = section?.querySelector('.ell-offers__input:checked');
    const bar = section?.querySelector('.ell-sticky');
    if (!(picked instanceof HTMLInputElement) || !bar) return;

    const { price, compare, percent } = picked.dataset;
    const amount = bar.querySelector('[data-ell-amount]');
    const was = bar.querySelector('[data-ell-compare]');
    const tag = bar.querySelector('[data-ell-badge]');

    if (amount && price) amount.textContent = price;
    if (was && compare) was.textContent = compare;
    // the badge carries its words, with [percent] where the number goes
    const words = tag instanceof HTMLElement ? tag.dataset.ellBadge : '';
    if (tag && words && percent) tag.textContent = words.replace('[percent]', percent);
  }

  /** @param {import('@shopify/events').ProductSelectEvent} event */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const section = this.closest('.shopify-section');
    if (!section) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;

        for (const live of section.querySelectorAll('[data-ell-live]')) {
          if (!(live instanceof HTMLElement)) continue;

          const fresh = detail.html.querySelector(`[data-ell-live="${live.dataset.ellLive}"]`);
          if (fresh) live.replaceChildren(...fresh.cloneNode(true).childNodes);
        }
        this.#showOffer();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[ell-product] Event promise rejected:', error);
      });
  };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * The day the offer ends (snippets/ell-deal.liquid). The offer ends every night, and the
 * date is on the page when it is rendered. This prints it again from the visitor's clock,
 * so a cached page or a tab left open past midnight still says tonight. The element
 * carries its words, with [date] where the date goes, and the offset from UTC of the
 * clock the offer ends on.
 *
 * @extends {Component}
 */
class EllDealEnds extends Component {
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.#show();
    this.#timer = window.setInterval(() => this.#show(), 60000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.#timer);
  }

  #show() {
    const zone = /^([+-])(\d{2})(\d{2})$/.exec(this.dataset.zone ?? '');
    const { words } = this.dataset;
    if (!zone || !words) return;

    const minutes = (Number(zone[2]) * 60 + Number(zone[3])) * (zone[1] === '-' ? -1 : 1);
    const there = new Date(Date.now() + minutes * 60000);
    this.textContent = words.replace('[date]', `${MONTHS[there.getUTCMonth()]} ${there.getUTCDate()}`);
  }
}

/**
 * Keeps aria-expanded of a row's summary in step with its details element.
 * The toggle event does not bubble, so it is caught on the way down.
 */
document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.classList.contains('ell-row__details')) return;

    details.querySelector('summary')?.setAttribute('aria-expanded', String(details.open));
  },
  { capture: true }
);

if (!customElements.get('ell-product-component')) {
  customElements.define('ell-product-component', EllProduct);
}

if (!customElements.get('ell-gallery-component')) {
  customElements.define('ell-gallery-component', EllGallery);
}

if (!customElements.get('ell-sticky-component')) {
  customElements.define('ell-sticky-component', EllSticky);
}

if (!customElements.get('ell-deal-ends')) {
  customElements.define('ell-deal-ends', EllDealEnds);
}
